import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Container, Input, Row } from "reactstrap";
import { format } from "date-fns";
import cronstrue from "cronstrue";

import Schedule from "Pages/Setup/Scheduler/Components/Schedule/Schedule";
import styles from "Pages/Setup/Scheduler/Scheduler.module.css";
import AddSchedule from "Pages/Setup/Scheduler/Components/AddSchedule/AddSchedule";
import DeleteSchedule from "Pages/Setup/Scheduler/Components/DeleteSchedule/DeleteSchedule";
import EditSchedule from "Pages/Setup/Scheduler/Components/EditSchedule/EditSchedule";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import useHelpers from "Hooks/useHelpers";
import { convert } from "crontzconvert";
import usePageTitle from "Hooks/usePageTitle";

export interface ISchedule {
    name: string;
    originalName?: string;
    description?: string;
    nextRun: string;
    // Integrations have an order, and do not run parallel
    integrations: string[];
    // cron expression https://docs.aws.amazon.com/AmazonCloudWatch/latest/events/ScheduledEvents.html#CronExpressions
    // or rate expression. rate expressions are enclosed in "rate()". Example: "rate(5 minutes)"
    trigger: string;
    enabled: boolean;
    start_date: string;
}

/** Default value for schedule, the schedule that will be added */
export const defaultSchedule = {
    name: "",
    originalName: undefined,
    nextRun: "",
    integrations: ["Select Integration"],
    // This defaults to rate so that CronSelector.tsx and cronstrue do not attempt to render and process the trigger
    trigger: "",
    enabled: true,
    start_date: "",
};

interface IData {
    schedules: ISchedule[];
    errors: string[];
}

export const PAUSED_FLAG = "PAUSED.";

export type ModalType = "edit" | "add" | "delete" | "";

/** Page for view schedule link in Setup dropdown */
export default function Scheduler() {
    const [pageData, setPageData] = useState<IData>();
    const [integrations, setIntegrations] = useState<IIntegration[]>([]);
    const [activeModal, setActiveModal] = useState<ModalType>("");
    /** Holds the schedule to be used for edit/delete modals when edit/delete buttons are clicked */
    const [scheduleClicked, setScheduleClicked] =
        useState<ISchedule>(defaultSchedule);
    const [searchTerm, setSearchTerm] = useState("");

    usePageTitle("Schedules");
    const { get, post } = useFetch();
    const { newAlert } = useAlertContext();
    const { canEditSchedule } = useHelpers();

    const ignoreGetSchedules = useRef(false);

    /** Fetches schedules. Called inside useEffect below to fetch on page load. This is wrapped in
     * useCallback and outside the useEffect so it can be passed to child components and called
     * there to update state here.
     */
    const getSchedules = useCallback(async () => {
        const response = await get("/api/schedules");

        let data: IData | Response;
        try {
            data = await response.json();
        } catch (e) {
            data = response;
        }
        console.log("Response for get schedules request: ", data);

        if (response.status === 403) {
            newAlert({ message: "The user does not have permission to view schedules" });
            return;
        }

        if (
            response.ok &&
            !ignoreGetSchedules.current &&
            "schedules" in data &&
            "errors" in data
        ) {
            const dataWithTriggersInLocalTz = data.schedules.map((s) => {
                const triggerInLocalTz = s.trigger
                    .split(";")
                    .map((t) =>
                        t.startsWith("rate")
                            ? t
                            : convertCronFromMountainToUserTz(t)
                    )
                    .join(";");
                return { ...s, trigger: triggerInLocalTz };
            });

            setPageData({ ...data, schedules: dataWithTriggersInLocalTz });

            if (!data.schedules.length && data.errors.length) {
                newAlert({ message: "Error loading schedules" });
            } else if (data.schedules.length && data.errors.length) {
                newAlert({
                    message: `${data.errors.length} schedules failed to load`,
                });
            }
        }

        function convertCronFromMountainToUserTz(cron: string) {
            const mountainTimezone = "America/Denver";
            const userTimezone =
                Intl.DateTimeFormat().resolvedOptions().timeZone;
            let cronInUserTz = convert(cron, mountainTimezone, userTimezone);
            // remove seconds added by crontzconvert and not used in react-js-cron
            cronInUserTz = cronInUserTz.split(" ").slice(1).join(" ");

            return cronInUserTz;
        }

        return () => {
            ignoreGetSchedules.current = true;
        };
    }, [get, newAlert]);

    // Fetch schedules from back end endpoint
    useEffect(() => {
        ignoreGetSchedules.current = false;
        getSchedules();
        return () => {
            ignoreGetSchedules.current = true;
        };
    }, [get, getSchedules]);

    // Get integrations and arguments for Add/Edit Schedule modals
    useEffect(() => {
        let ignore = false;

        getIntegrations();

        async function getIntegrations() {
            const response = await get("/api/integrations");

            let data: IIntegration[] | Response;
            try {
                data = await response.json();
            } catch (e) {
                console.log(e);
                data = response;
            }
            console.log("Integrations request response: ", data);

            if (response.ok && !ignore) {
                setIntegrations(data as IIntegration[]);
            }
        }

        return () => {
            ignore = true;
        };
    }, [get]);

    const disableAllSchedules = async () => {
        const response = await post("/api/schedules/disable_all");

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Response for disable all schedules request: ", data);

        if (response.ok) {
            if (data.errors) {
                newAlert({
                    message: "Some schedules failed to disable",
                    color: "danger",
                });
            } else {
                newAlert({
                    message: "Disabled all schedules",
                    color: "success",
                });
            }
            getSchedules();
        } else {
            newAlert({ message: "Error disabling all schedules" });
        }
    };

    const enableAllSchedules = async () => {
        const response = await post("/api/schedules/enable_all");

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Response for enable all schedules request: ", data);

        if (response.ok) {
            if (data.errors) {
                newAlert({
                    message: "Some schedules failed to enable",
                    color: "danger",
                });
            } else {
                newAlert({
                    message: "Enabled all schedules",
                    color: "success",
                });
            }
            getSchedules();
        } else {
            newAlert({ message: "Error enabling all schedules" });
        }
    };

    const reenablePausedSchedules = async () => {
        const response = await post("/api/schedules/reenable_paused");

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Response for reenabled paused schedules request: ", data);

        if (response.ok) {
            newAlert({
                message: "Re-enabled all paused schedules",
                color: "success",
            });
            getSchedules();
        } else {
            newAlert({ message: "Error re-enabling paused schedules" });
        }
    };

    /** Onchange handler for schedule search input. Updates searchTerm state and changes schedulesShown */
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    /** Results from filtering schedules with search input term. This is mapped in JSX below to display
     * the correct schedules.
     */
    const schedulesShown = pageData?.schedules.filter((s) => {
        const nextRun = format(
            new Date(s.nextRun),
            "EEE, MMM d, y, HH:mm"
        ).toLowerCase();

        // ";" separates multiple cron expressions - needed for conversion to AWS EventBridge Scheduler
        // because Windows Task Scheduler had multiple triggers per schedule while EventBridge only
        // allows one "trigger" (as a cron expression) per schedule
        const trigger = s.trigger
            .split(";")
            .map((t) =>
                t.startsWith("rate")
                    ? t
                    : cronstrue.toString(t, {
                          verbose: true,
                          use24HourTimeFormat: true,
                      })
            )
            .join(" ");

        return (
            s.name
                .replaceAll("_", " ")
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
            s.integrations.some((i) =>
                i.toLowerCase().includes(searchTerm.toLowerCase())
            ) ||
            trigger.toLowerCase().includes(searchTerm.toLowerCase()) ||
            nextRun.includes(searchTerm.toLowerCase())
        );
    });

    /** State setting callback passed to child. The wrapper function creates a closure so
     * the child can call it, but state will be updated here in the parent.
     */
    const onSetActiveModal = (modal: ModalType) => {
        setActiveModal(modal);
    };

    const onSetScheduleClicked = (schedule: ISchedule) => {
        const originalName = !schedule.originalName
            ? schedule.name
            : schedule.originalName;
        const newSchedule = { ...schedule, originalName };
        setScheduleClicked(newSchedule);
    };

    // Schedule prop is added in the JSX below since it comes from a map statement
    const scheduleProps = {
        getSchedules,
        integrations,
        onSetActiveModal,
        onSetScheduleClicked,
        scheduleClicked,
    };

    const addScheduleProps = {
        activeModal,
        getSchedules,
        integrations,
        onSetActiveModal,
        schedules: pageData?.schedules || [],
    };

    const editScheduleProps = {
        activeModal,
        getSchedules,
        integrations,
        onSetActiveModal,
        onSetScheduleClicked,
        scheduleClicked,
        schedules: pageData?.schedules || [],
    };

    const deleteScheduleProps = {
        activeModal,
        getSchedules,
        onSetActiveModal,
        scheduleClicked,
        onSetScheduleClicked,
    };

    return (
        <Container fluid className={styles.container}>
            {/* h1 title, search input, and new/enable/disable buttons box */}
            <div className={styles.titleBox}>
                <h1>Integration Schedules</h1>

                {/* Search Schedules Input */}
                <Input
                    className={styles.search}
                    type="search"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    autoFocus
                />

                {/* New / Disable All / Enable All Buttons */}
                {canEditSchedule() && (
                    <div className={styles.buttonsBox}>
                        <Button
                            color="primary"
                            onClick={() => setActiveModal("add")}
                        >
                            New Schedule
                        </Button>
                        <Button
                            color="danger"
                            onClick={disableAllSchedules}
                            disabled={!pageData}
                        >
                            Disable All
                        </Button>
                        <Button
                            color="success"
                            onClick={enableAllSchedules}
                            disabled={!pageData}
                        >
                            Enable All
                        </Button>
                        <Button
                            color="info"
                            onClick={reenablePausedSchedules}
                            disabled={
                                !pageData ||
                                !pageData.schedules.some((s) =>
                                    s.description?.startsWith(PAUSED_FLAG)
                                )
                            }
                        >
                            Reenable paused
                        </Button>
                    </div>
                )}
            </div>

            {/* Schedules */}
            <Row className={styles.scheduleBox}>
                {schedulesShown &&
                    schedulesShown.map((s) => (
                        <Schedule
                            key={s.name}
                            schedule={s}
                            {...scheduleProps}
                        />
                    ))}
            </Row>

            {/* Modals */}
            <AddSchedule {...addScheduleProps} />
            <EditSchedule {...editScheduleProps} />
            <DeleteSchedule {...deleteScheduleProps} />
        </Container>
    );
}
